/**
 * @module NotificationPreferencesPage
 * @package @geenius/notifications-react-css
 * @description Renders the full-page notification preference settings screen
 * for the React vanilla CSS variant.
 */

import { FadeIn } from '@geenius/motion/react-css'
import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  buildNotificationsSeoMeta,
  DEFAULT_CHANNEL_PREFS,
  DEFAULT_TYPE_PREFS,
} from '@geenius/notifications/shared'
import { SEOHead } from '@geenius/seo/react-css'
import type { JSX } from 'react'
import { NotificationPreferencesForm } from '../components/NotificationPreferencesForm'
import { useNotificationPreferences } from '../hooks/useNotificationPreferences'
import { cx } from '../lib/cx'
import { useNotificationCopy } from '../lib/notificationCopy'

/**
 * Props for the React CSS notification preferences page.
 */
export interface NotificationPreferencesPageProps {
  preferences: NotificationPreferences | null | undefined
  onUpdate?: (
    channels?: Record<NotificationChannel, boolean>,
    types?: Record<NotificationType, boolean>,
    quietHours?: { start: string; end: string },
  ) => Promise<void>
  className?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']
const TYPES: NotificationType[] = [
  'info',
  'success',
  'warning',
  'error',
  'mention',
  'invite',
  'payment',
  'system',
]

const LOADING_PREF_KEYS = [
  'notification-preferences-row-1',
  'notification-preferences-row-2',
  'notification-preferences-row-3',
  'notification-preferences-row-4',
  'notification-preferences-row-5',
  'notification-preferences-row-6',
  'notification-preferences-row-7',
  'notification-preferences-row-8',
]

/**
 * Renders the full-page notification preferences experience.
 *
 * @param props Page props containing current preferences and update callback.
 * @returns The preferences page markup.
 */
export function NotificationPreferencesPage({
  preferences: rawPreferences,
  onUpdate,
  className,
}: NotificationPreferencesPageProps): JSX.Element {
  const { t } = useNotificationCopy()
  const { preferences, isLoading, toggleChannel, toggleType, setQuietHours } =
    useNotificationPreferences(rawPreferences, onUpdate)

  if (isLoading) {
    return (
      <div className={cx('notifications__page', 'notifications__page-prefs', className)}>
        {LOADING_PREF_KEYS.map((key) => (
          <div
            key={key}
            className={cx('notifications__skeleton', 'notifications__skeleton--pref')}
          />
        ))}
      </div>
    )
  }

  const resolvedPreferences = preferences ?? {
    userId: '',
    channels: { ...DEFAULT_CHANNEL_PREFS },
    types: { ...DEFAULT_TYPE_PREFS },
    quietHours: { start: '22:00', end: '07:00' },
  }

  const seoMeta = buildNotificationsSeoMeta({
    title: t('notifications.seo.preferencesTitle'),
    description: t('notifications.seo.preferencesDescription'),
    path: '/notifications/preferences',
    keywords: ['notifications', 'preferences', 'channels', 'quiet hours'],
  })

  return (
    <>
      <SEOHead meta={seoMeta} />
      <div className={cx('notifications__page', 'notifications__page-prefs', className)}>
        <FadeIn>
          
            <h1 className={cx('notifications__page-title')}>
              {t('notifications.preferences.pageTitle')}
            </h1>
            <p className={cx('notifications__page-subtitle')} style={{ marginBottom: '2rem' }}>
              {t('notifications.preferences.pageDescription')}
            </p>
          
        </FadeIn>
        <FadeIn delay={80}>
          <NotificationPreferencesForm
            preferences={resolvedPreferences}
            onUpdate={async (channels, types, quietHours) => {
              if (channels) {
                for (const channel of CHANNELS) {
                  if (resolvedPreferences.channels[channel] !== channels[channel]) {
                    await toggleChannel(channel, channels[channel])
                  }
                }
              }

              if (types) {
                for (const type of TYPES) {
                  if (resolvedPreferences.types[type] !== types[type]) {
                    await toggleType(type, types[type])
                  }
                }
              }

              if (quietHours) {
                await setQuietHours(quietHours.start, quietHours.end)
              }
            }}
          />
        </FadeIn>
      </div>
    </>
  )
}
